import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { CalendarClock } from 'lucide-react';

import { LegacyTag, isLegacy } from '../../scheduling/MeetingActions';

import { useFetchMeetings } from '../../../../services/onboardingBoard';

// Meeting alerts (Nic 2026-09-24, reworked 2026-10-05): ONE reminder per
// meeting, with Call now and Open, while the board is open. It nudges at 15,
// 10 and 5 minutes: still on screen = it updates in place (never a second
// copy); closed with the X = it comes back at the next mark. It stays until
// the X (Open and Call now leave it up). Each mark fires once per meeting,
// even across a reload (session storage), so a refresh never re-nags.
const THRESHOLDS = [
  { mins: 15, floor: 10 },
  { mins: 10, floor: 5 },
  { mins: 5, floor: 0 },
];
const FIRED_KEY = 'os-meeting-alerts-v1';

const readFired = () => {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(FIRED_KEY) || '[]');
    return new Set(Array.isArray(v) ? v : []);
  } catch (_) {
    return new Set();
  }
};
const writeFired = (set) => {
  try {
    // Keep it small: only the latest few hundred keys matter.
    window.sessionStorage.setItem(
      FIRED_KEY,
      JSON.stringify([...set].slice(-300)),
    );
  } catch (_) {
    // ignore
  }
};

const nameOf = (m) =>
  [m.leadFirstName, m.leadLastName].filter(Boolean).join(' ') || 'your meeting';

// The headline keeps itself current while the reminder sits open (Nic
// 2026-10-05): it counts down, then says the meeting started, then that it
// ended. Ticks every 15 seconds on its own.
const headlineOf = (m, now) => {
  const toStart = moment(m.startTime).diff(moment(now), 'minutes', true);
  if (toStart > 0) return `Meeting in ${Math.max(1, Math.ceil(toStart))} min`;
  if (moment(now).isBefore(moment(m.endTime || m.startTime))) {
    const ago = Math.floor(-toStart);
    return ago < 1 ? 'Meeting now' : `Meeting started ${ago} min ago`;
  }
  return 'Meeting ended';
};

// A video meeting's first button is Join (Nic 2026-10-27: "easy to join"),
// opening its RingCentral Video room in a new tab; a phone call's is Call now.
const isVideo = (m) => m?.kind === 'video' && !!m.videoUrl;

export const AlertBody = ({ m, onCall, onOpen, actionable }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="min-w-0">
      <div className="text-sm font-semibold">
        {headlineOf(m, now)}: {nameOf(m)}
      </div>
      <div className="text-xs opacity-80">
        {moment(m.startTime).local().format('h:mm A')}
        {m.eventTypeName ? ` · ${m.eventTypeName}` : ''}
        {isLegacy(m) ? <LegacyTag className="ml-1.5 !text-slate-300 !ring-white/20" /> : null}
      </div>
      {actionable ? (
        <div className="mt-2 flex gap-2">
          {isVideo(m) ? (
            <a
              href={m.videoUrl}
              target="_blank"
              rel="noreferrer"
              className="whitespace-nowrap rounded-md bg-sky-600 px-2.5 py-1 text-xs font-semibold text-white no-underline hover:bg-sky-500 hover:text-white"
            >
              Join
            </a>
          ) : (
            <button
              type="button"
              onClick={() => {
                onCall(m);
              }}
              className="whitespace-nowrap rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-500"
            >
              Call now
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onOpen(m);
            }}
            className="whitespace-nowrap rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-200"
          >
            Open
          </button>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Loads today and tomorrow's meetings for the viewer, fires the alerts, and
 * hands back what the Meetings button shows: today's count and the next one.
 */
const useMeetingAlerts = ({
  enabled = true,
  now,
  onCall,
  onOpen,
  apiBase = '/onboarding-board',
  // Which meetings can Call now / Open (the board: ones with a run).
  canAct = (m) => !!m.cadenceRunId,
}) => {
  const dayKey = moment(now).local().format('YYYY-MM-DD');
  const range = useMemo(() => {
    const start = moment(dayKey, 'YYYY-MM-DD').startOf('day');
    return {
      from: start.toISOString(),
      to: start.clone().add(2, 'days').toISOString(),
    };
  }, [dayKey]);
  const q = useFetchMeetings({ ...range, apiBase }, { enabled });
  const meetings = useMemo(
    () => (q.data?.meetings || []).filter((m) => m.status === 'active'),
    [q.data],
  );

  const fired = useRef(null);
  if (fired.current === null) fired.current = readFired();
  const handlers = useRef({ onCall, onOpen, canAct });
  handlers.current = { onCall, onOpen, canAct };

  useEffect(() => {
    if (!enabled) return;
    let changed = false;
    meetings.forEach((m) => {
      const mins = moment(m.startTime).diff(moment(now), 'minutes', true);
      THRESHOLDS.forEach((t) => {
        const key = `${m.calendlyBookingId}:${t.mins}`;
        if (mins > t.mins || mins <= t.floor || fired.current.has(key)) return;
        fired.current.add(key);
        changed = true;
        const toastId = `meeting-${m.calendlyBookingId}`;
        const render = () => (
          <AlertBody
            m={m}
            onCall={(x) => handlers.current.onCall(x)}
            onOpen={(x) => handlers.current.onOpen(x)}
            actionable={handlers.current.canAct(m)}
          />
        );
        // Still up: refresh it in place. Closed (or never shown): show it.
        if (toast.isActive(toastId)) {
          toast.update(toastId, { render });
          return;
        }
        toast.info(render, {
          toastId,
          icon: <CalendarClock size={18} />,
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        });
      });
    });
    if (changed) writeFired(fired.current);
  }, [enabled, meetings, now]);

  const today = moment(now).local();
  const upcoming = meetings.filter(
    (m) =>
      !m.outcome &&
      moment(m.startTime).isAfter(moment(now).subtract(60, 'minutes')),
  );
  return {
    // What is still ahead today (Nic 2026-09-24): a meeting that already
    // ended, or has an outcome recorded, no longer counts.
    todayCount: meetings.filter(
      (m) =>
        !m.outcome &&
        moment(m.startTime).local().isSame(today, 'day') &&
        moment(m.endTime || m.startTime).isAfter(moment(now)),
    ).length,
    next: upcoming[0] || null,
    // Today and tomorrow's active meetings (the board's pause banner names
    // the meeting that is pausing it).
    meetings,
  };
};

export default useMeetingAlerts;
